import React, { useEffect, useRef, useState } from 'react';
import CommonModal from './CommonModal';
import AddDeliveryAddressModal from './AddDeliveryAddressModal';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import { getSessionId } from '../../utils/sessionId';
import { HTTP_STATUS } from '../../utils/httpStatusCodes';
import '../../styles/upfrontOrderSetup.css';

const FALLBACK_SERVICES = [{ type: 'take_away', name: 'Take Away' }];

const minPreOrderDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 2);
  return date.toISOString().split('T')[0];
};

const customerFromSuggestion = (suggestion) => {
  const c = suggestion?.customerObject || {};
  return {
    id: c.id,
    name: [c.firstName, c.lastName].filter(Boolean).join(' ') || suggestion?.label || 'Customer',
    phone: c.contactNumber || '',
    email: c.email || '',
  };
};

/**
 * The popup the Takeaway / Delivery tab opens with when Settings > 'Customer details first (takeaway & delivery)' is on: the service
 * type and time, the customer and (for delivery) the delivery address, in one place. It uses the same endpoints as Order Review
 * (service types, time slots), Select Customer (search, save) and Delivery Address (list, save), and hands the answer to onSave;
 * utils/upfrontSetup.js keeps it for the order and places the order from it.
 *
 * It cannot be dismissed by accident (no backdrop / Escape close): the staff member either starts the order or chooses "Use the usual
 * flow", which runs this order the way takeaway / delivery has always run (onSkip).
 */
const UpfrontOrderSetupModal = ({ isOpen, initial = null, onSave, onSkip }) => {
  const [services, setServices] = useState(FALLBACK_SERVICES);
  const [service, setService] = useState(initial?.service || 'take_away');
  const [day, setDay] = useState(initial?.day || 'today');
  const [date, setDate] = useState(initial?.date || minPreOrderDate());
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState(initial?.time || '');

  const [customer, setCustomer] = useState(initial?.customer || null);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ first_name: '', last_name: '', number: '', mail: '' });
  const [customerErrors, setCustomerErrors] = useState({});
  const [savingCustomer, setSavingCustomer] = useState(false);

  const [addresses, setAddresses] = useState([]);
  const [addressId, setAddressId] = useState(initial?.address?.id || null);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const searchSeq = useRef(0);

  const delivery = service === 'delivery';

  // service types the restaurant offers (the same list Order Review shows)
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await callAPI(`${apiUrls.GET_OERDER_TYPE}?session_id=${getSessionId()}`, {}, 'GET');
        const list = res?.data?.data || res?.data || [];
        if (!cancelled && Array.isArray(list) && list.length > 0) setServices(list);
      } catch {
        // keep the fallback (take away)
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // time slots for the chosen service and day (the same call Order Review makes)
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const value = day === 'other' ? date : day;
        const res = await callAPI(`${apiUrls.GET_TIME_SLOTS}?type=${service}&day=${value}&session_id=${getSessionId()}`, {}, 'GET');
        const list = res?.data?.data || res?.data || [];
        if (cancelled) return;
        const clean = Array.isArray(list) ? list : [];
        setSlots(clean);
        setTime((current) => (clean.some((s) => s.slot === current) ? current : clean[0]?.slot || ''));
      } catch {
        if (!cancelled) {
          setSlots([]);
          setTime('');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, service, day, date]);

  // customer search (name, e-mail or phone)
  useEffect(() => {
    if (!isOpen) return undefined;
    if (!query || query.length < 2) {
      setSuggestions([]);
      return undefined;
    }
    const seq = ++searchSeq.current;
    const timer = setTimeout(async () => {
      try {
        const res = await callAPI(`${apiUrls.CUSTOMER_AUTOCOMPLETE}?term=${encodeURIComponent(query)}`, {}, 'GET');
        if (seq !== searchSeq.current) return;
        setSuggestions(res?.data?.status === 1 && Array.isArray(res.data.data) ? res.data.data : []);
      } catch {
        if (seq === searchSeq.current) setSuggestions([]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [isOpen, query]);

  const loadAddresses = async (customerId, keepSelection = true) => {
    try {
      const res = await callAPI(apiUrls.CUSTOMER_DELIVERY_ADDRESS, { current_customer_id: customerId }, 'GET');
      if (res?.status === HTTP_STATUS.OK) {
        const list = res.data?.data?.delivery_address || [];
        setAddresses(list);
        setAddressId((current) => (keepSelection && list.some((a) => a.id === current) ? current : list[0]?.id || null));
      }
    } catch {
      setAddresses([]);
      setAddressId(null);
    }
  };

  // a delivery order needs one of this customer's addresses
  useEffect(() => {
    if (!isOpen || !delivery || !customer?.id) {
      setAddresses([]);
      if (!delivery) setAddressId(null);
      return;
    }
    loadAddresses(customer.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, delivery, customer?.id]);

  const chooseCustomer = (chosen) => {
    setCustomer(chosen);
    setQuery('');
    setSuggestions([]);
    setShowNewCustomer(false);
    setAddressId(null);
  };

  const saveNewCustomer = async () => {
    setCustomerErrors({});
    if (!newCustomer.first_name && !newCustomer.last_name && !newCustomer.number) {
      setCustomerErrors({ first_name: 'First name or phone number is required.' });
      return;
    }
    setSavingCustomer(true);
    try {
      const payload = {
        ...newCustomer,
        subscribed: false,
        mobile: newCustomer.number,
        name: `${newCustomer.first_name} ${newCustomer.last_name}`.trim(),
      };
      const res = await callAPI(apiUrls.SAVE_CUSTOMER, {}, 'POST', payload, {});
      if (res?.data?.errors) {
        setCustomerErrors(res.data.errors);
        return;
      }
      // a phone number that already belongs to a customer here: continue with their existing profile
      if (res?.data?.status === 409 && res.data.customer) {
        chooseCustomer({ id: res.data.customer, name: payload.name || 'Customer', phone: newCustomer.number, email: newCustomer.mail });
        return;
      }
      if (res?.data?.status === 1 && res.data.customer) {
        chooseCustomer({ id: res.data.customer, name: payload.name || 'Customer', phone: newCustomer.number, email: newCustomer.mail });
        setNewCustomer({ first_name: '', last_name: '', number: '', mail: '' });
        return;
      }
      setCustomerErrors({ general: res?.data?.message || 'Could not save the customer.' });
    } catch {
      setCustomerErrors({ general: 'Could not save the customer.' });
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleAddAddress = async (formData) => {
    formData.selected_customer_id = customer?.id;
    try {
      const res = await callAPI(apiUrls.SAVE_DELIVERY_ADDRESS, {}, 'POST', formData, {});
      if (res?.status === HTTP_STATUS.OK) {
        if (res.data?.errors) return { success: false, errors: res.data.errors };
        await loadAddresses(customer.id, false);
        return { success: true };
      }
      return { success: false, errors: { general: ['Something went wrong. Please try again.'] } };
    } catch (error) {
      const errors = error?.response?.data?.data?.errors;
      return { success: false, errors: errors || { general: ['Something went wrong. Please try again.'] } };
    }
  };

  const chosenAddress = addresses.find((a) => a.id === addressId) || null;
  const timeOk = slots.length === 0 || !!time;
  const canStart = !!service && timeOk && (!delivery || (!!customer?.id && !!chosenAddress));

  const start = () => {
    if (!canStart) return;
    onSave({
      service,
      day,
      date: day === 'other' ? date : null,
      time,
      customer: customer ? { id: customer.id, name: customer.name, phone: customer.phone || '', email: customer.email || '' } : null,
      address: delivery && chosenAddress ? { id: chosenAddress.id, full_address: chosenAddress.full_address } : null,
    });
  };

  const missingHint = () => {
    if (!timeOk) return 'Choose a time.';
    if (delivery && !customer?.id) return 'A delivery order needs a customer.';
    if (delivery && !chosenAddress) return customer?.id ? 'Choose or add a delivery address.' : '';
    return '';
  };

  return (
    <>
      <CommonModal isOpen={isOpen} onClose={() => {}} elId="upfrontOrderSetupModal" closeOnBackdropClick={false} closeOnEscape={false}>
        <div className="modal-header">
          <h1 className="modal-title mdTitle" id="upfrontOrderSetupLabel">New takeaway / delivery order</h1>
        </div>

        <div className="modal-body">
          <div className="modalBodyStyle upfrontSetup">
            <section className="upfrontSection">
              <h2 className="upfrontHeading">1. Service and time</h2>
              <div className="upfrontChoices">
                {services.map((s) => (
                  <button
                    type="button"
                    key={s.type}
                    className={`btn upfrontChoice ${service === s.type ? 'btnPrimary' : 'btnLight'}`}
                    onClick={() => setService(s.type)}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
              <div className="upfrontRow">
                <div className="upfrontField">
                  <label className="form-label" htmlFor="upfrontDay">Day</label>
                  <select id="upfrontDay" className="form-select" value={day} onChange={(e) => setDay(e.target.value)}>
                    <option value="today">Today</option>
                    <option value="tomorrow">Tomorrow</option>
                    <option value="other">Pre-Order</option>
                  </select>
                </div>
                {day === 'other' && (
                  <div className="upfrontField">
                    <label className="form-label" htmlFor="upfrontDate">Date</label>
                    <input id="upfrontDate" type="date" className="form-control" value={date} min={minPreOrderDate()} onChange={(e) => setDate(e.target.value)} />
                  </div>
                )}
                <div className="upfrontField">
                  <label className="form-label" htmlFor="upfrontTime">Time</label>
                  <select id="upfrontTime" className="form-select" value={time} onChange={(e) => setTime(e.target.value)}>
                    <option value="">-- Please Select Time --</option>
                    {slots.map((slot, index) => (
                      <option key={index} value={slot.slot}>
                        {slot.slot}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            <section className="upfrontSection">
              <h2 className="upfrontHeading">2. Customer</h2>
              {customer ? (
                <div className="upfrontSelected">
                  <div>
                    <strong>{customer.name}</strong>
                    <div className="upfrontMuted">{[customer.phone, customer.email].filter(Boolean).join(' · ')}</div>
                  </div>
                  <button type="button" className="btn btnLight" onClick={() => setCustomer(null)}>
                    Change
                  </button>
                </div>
              ) : (
                <>
                  <div className="upfrontSearch">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by name, email or phone"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                    {suggestions.length > 0 && (
                      <ul className="upfrontSuggestions">
                        {suggestions.map((s, index) => (
                          <li key={s.customerObject?.id ?? index}>
                            <button type="button" onClick={() => chooseCustomer(customerFromSuggestion(s))}>
                              {s.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {!showNewCustomer && (
                    <div className="upfrontRow upfrontLinks">
                      <button type="button" className="btn btnLight" onClick={() => setShowNewCustomer(true)}>
                        Add new customer
                      </button>
                      {!delivery && <span className="upfrontMuted">No customer selected: the order is for a guest.</span>}
                    </div>
                  )}
                  {showNewCustomer && (
                    <div className="upfrontNewCustomer">
                      <div className="upfrontRow">
                        <div className="upfrontField">
                          <input type="text" className="form-control" placeholder="First name" value={newCustomer.first_name} onChange={(e) => setNewCustomer({ ...newCustomer, first_name: e.target.value })} />
                          {customerErrors.first_name && <div className="upfrontError">{customerErrors.first_name}</div>}
                        </div>
                        <div className="upfrontField">
                          <input type="text" className="form-control" placeholder="Last name" value={newCustomer.last_name} onChange={(e) => setNewCustomer({ ...newCustomer, last_name: e.target.value })} />
                        </div>
                      </div>
                      <div className="upfrontRow">
                        <div className="upfrontField">
                          <input type="tel" className="form-control" placeholder="Phone number" value={newCustomer.number} onChange={(e) => setNewCustomer({ ...newCustomer, number: e.target.value })} />
                          {customerErrors.number && <div className="upfrontError">{[].concat(customerErrors.number).join(' ')}</div>}
                        </div>
                        <div className="upfrontField">
                          <input type="email" className="form-control" placeholder="Email address" value={newCustomer.mail} onChange={(e) => setNewCustomer({ ...newCustomer, mail: e.target.value })} />
                          {customerErrors.mail && <div className="upfrontError">{[].concat(customerErrors.mail).join(' ')}</div>}
                        </div>
                      </div>
                      {customerErrors.general && <div className="upfrontError">{customerErrors.general}</div>}
                      <div className="upfrontRow upfrontLinks">
                        <button type="button" className="btn btnLight" onClick={() => { setShowNewCustomer(false); setCustomerErrors({}); }}>
                          Cancel
                        </button>
                        <button type="button" className="btn btnPrimary" onClick={saveNewCustomer} disabled={savingCustomer}>
                          {savingCustomer ? 'Saving...' : 'Save customer'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>

            {delivery && (
              <section className="upfrontSection">
                <h2 className="upfrontHeading">3. Delivery address</h2>
                {!customer?.id && <p className="upfrontMuted">Choose the customer first.</p>}
                {customer?.id && (
                  <>
                    {addresses.length === 0 && <p className="upfrontMuted">No delivery addresses found for this customer.</p>}
                    {addresses.map((address, index) => (
                      <div className="form-check mb-2" key={address.id}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="upfrontAddress"
                          id={`upfrontAddress${index}`}
                          checked={addressId === address.id}
                          onChange={() => setAddressId(address.id)}
                        />
                        <label className="form-check-label" htmlFor={`upfrontAddress${index}`}>
                          <span>{address.full_address}</span>
                          {address.distance_from_restaurant_in_kms > 0 && (
                            <span className="upfrontMuted"> · {parseFloat(address.distance_from_restaurant_in_kms).toFixed(2)} Kms</span>
                          )}
                        </label>
                      </div>
                    ))}
                    <button type="button" className="btn btnLight mt-2" onClick={() => setAddressModalOpen(true)}>
                      Add delivery address
                    </button>
                  </>
                )}
              </section>
            )}
          </div>
        </div>

        <div className="modal-footer justify-content-between">
          <button type="button" className="btn btnLight" onClick={onSkip}>
            Use the usual flow
          </button>
          <div className="upfrontFooterRight">
            {!canStart && <span className="upfrontMuted me-3">{missingHint()}</span>}
            <button type="button" className="btn btnPrimary" onClick={start} disabled={!canStart}>
              Start order
            </button>
          </div>
        </div>
      </CommonModal>

      <AddDeliveryAddressModal
        isOpen={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        onSubmit={handleAddAddress}
        customerInfo={{
          first_name: (customer?.name || '').split(' ')[0] || '',
          last_name: (customer?.name || '').split(' ').slice(1).join(' '),
          email: customer?.email || '',
          mobile: customer?.phone || '',
        }}
      />
    </>
  );
};

export default UpfrontOrderSetupModal;
